import { useEffect, useRef } from 'react';
import { Bell, Check, Folder, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useNotificationStore from '../store/notificationStore';
import useCardPanelStore from '../store/cardPanelStore';
import formatRelative from '../utils/formatRelative';

function initials(name) {
  return name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
}

function Avatar({ name, url, size = 28 }) {
  return (
    <div
      className="rounded-full bg-accent/20 text-accent flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {url ? <img src={url} alt={name} className="w-full h-full rounded-full object-cover" /> : initials(name)}
    </div>
  );
}

export default function NotificationPanel({ onClose }) {
  const { list, markRead, markAllRead } = useNotificationStore();
  const openCard = useCardPanelStore((s) => s.openCard);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleClick = async (n) => {
    if (!n.read_at) markRead(n.id);
    if (n.link_type === 'card' && n.link_id) {
      openCard(n.link_id);
    } else if (n.link_type === 'board' && n.link_id) {
      navigate(`/board/${n.link_id}`);
    }
    onClose();
  };

  return (
    <div
      ref={ref}
      className="absolute top-full right-0 mt-1 w-[380px] max-h-[500px] bg-card border border-border rounded-lg shadow-2xl flex flex-col z-[60]"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
        <h3 className="text-sm font-semibold text-text-primary">Notificações</h3>
        {list.some((n) => !n.read_at) && (
          <button
            onClick={() => markAllRead()}
            className="flex items-center gap-1 text-[11px] text-text-secondary hover:text-accent transition-colors"
            title="Marcar todas como lidas"
          >
            <Check className="w-3 h-3" /> Marcar como lidas
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {list.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Bell className="w-10 h-10 text-text-secondary/30 mx-auto mb-2" />
            <div className="text-xs text-text-secondary">Nenhuma notificação</div>
          </div>
        ) : (
          list.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/30 hover:bg-white/[0.03] transition-colors ${!n.read_at ? 'bg-accent/[0.04]' : ''}`}
            >
              <Avatar name={n.actor_name} url={n.actor_avatar} size={32} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <span className="flex-1 text-xs text-text-primary font-medium line-clamp-2">{n.title}</span>
                  {!n.read_at && <span className="w-2 h-2 rounded-full bg-accent shrink-0 mt-1" />}
                </div>
                {n.body && <div className="text-[11px] text-text-secondary mt-0.5 line-clamp-1">{n.body}</div>}
                <div className="flex items-center gap-1.5 mt-1 text-[10px] text-text-secondary/80">
                  {n.link_type === 'board' && <Folder className="w-3 h-3" />}
                  {n.link_type === 'card' && <Layers className="w-3 h-3" />}
                  <span>{formatRelative(n.created_at)}</span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
